import React from "react";
import {
  Box, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Paper,
} from "@mui/material";
import EmptyState, { EmptyStateProps } from "./EmptyState";
import TableSkeleton from "./TableSkeleton";

export interface DataTableColumn<T> {
  id: string;
  label: React.ReactNode;
  align?: "left" | "center" | "right";
  width?: number | string;
  /** Custom cell renderer; defaults to row[id]. */
  render?: (row: T, index: number) => React.ReactNode;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  loading?: boolean;
  getRowKey?: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;

  /** Row selection. */
  selectable?: boolean;
  selected?: Array<string | number>;
  onSelectionChange?: (ids: Array<string | number>) => void;

  /** Right-aligned per-row actions cell. */
  actions?: (row: T) => React.ReactNode;
  actionsLabel?: string;

  /** Empty state (shown when !loading and rows is empty). */
  empty?: EmptyStateProps;

  /** Client pagination (optional). Omit for a plain scrolling table. */
  pagination?: boolean;
  page?: number;
  rowsPerPage?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  /** Total rows for server pagination; defaults to rows.length. */
  total?: number;
}

/**
 * Premium, rounded data table used across all INTALK list pages. Handles the
 * loading skeleton, empty state, optional selection, row actions and pagination
 * so pages only declare columns + rows.
 */
export default function DataTable<T extends Record<string, any>>({
  columns,
  rows,
  loading,
  getRowKey = (r: T, i: number) => (r.id ?? i) as string | number,
  onRowClick,
  selectable,
  selected = [],
  onSelectionChange,
  actions,
  actionsLabel = "Actions",
  empty,
  pagination,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  total,
}: DataTableProps<T>) {
  const colCount = columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0);

  const allKeys = rows.map((r, i) => getRowKey(r, i));
  const allSelected = selectable && rows.length > 0 && allKeys.every((k) => selected.includes(k));
  const someSelected = selectable && selected.length > 0 && !allSelected;

  const toggleAll = () => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? [] : allKeys);
  };
  const toggleOne = (key: string | number) => {
    if (!onSelectionChange) return;
    onSelectionChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };

  // Optional client-side pagination slice.
  const pagedRows =
    pagination && total === undefined
      ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : rows;

  const showEmpty = !loading && rows.length === 0;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "16px",
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
      }}
    >
      <TableContainer sx={{ borderRadius: 0, boxShadow: "none" }}>
        <Table>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={!!allSelected}
                    indeterminate={!!someSelected}
                    onChange={toggleAll}
                  />
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align} sx={{ width: col.width }}>
                  {col.label}
                </TableCell>
              ))}
              {actions && <TableCell align="right">{actionsLabel}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeleton columns={colCount} />}

            {showEmpty && (
              <TableRow>
                <TableCell colSpan={colCount} sx={{ border: 0 }}>
                  {empty ? <EmptyState {...empty} dense /> : <EmptyState title="Nothing here yet" dense />}
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              pagedRows.map((row, index) => {
                const key = getRowKey(row, index);
                const isSel = selectable && selected.includes(key);
                return (
                  <TableRow
                    key={key}
                    hover
                    selected={!!isSel}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={{ cursor: onRowClick ? "pointer" : "default" }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox size="small" checked={!!isSel} onChange={() => toggleOne(key)} />
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.id} align={col.align} sx={{ width: col.width }}>
                        {col.render ? col.render(row, index) : row[col.id]}
                      </TableCell>
                    ))}
                    {actions && (
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {actions(row)}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && !showEmpty && (
        <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
          <TablePagination
            component="div"
            count={total ?? rows.length}
            page={page}
            onPageChange={(_, p) => onPageChange?.(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => onRowsPerPageChange?.(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      )}
    </Paper>
  );
}
